import { Ingredient, IngredientReal, IngredientType } from '@cf-shared/data-model/ingredient';

/**
 * One row of the meal-photo scanner's result list, reduced to the parts that decide
 * whether it will end up in the meal.
 *
 * A structural type rather than the page's own `ResultItem`, because that interface is
 * private to the page and carries a dozen fields about how the row is DRAWN. Taking
 * only what the rule reads keeps this function testable without building a whole page.
 */
export interface ScannedRow {
  /**
   * The name the photo model gave the food. Two rows CAN share one -- the model returns a
   * row per food it sees, and one plate can hold two of the same thing -- so it is not a key.
   */
  aiName: string;
  matched: boolean;
  checked: boolean;
  foodId?: string;
  /** Only the name is read. Shaped as the page's own rows are, so they pass straight in. */
  foodHeader: { name: string };
  estimatedGrams: number;
}

/**
 * The ingredients the meal will hold BESIDES the one whose amount is being adjusted.
 *
 * The portion screen adds up whatever it is handed in `local.ingredients`, puts the food
 * being typed on top, and calls the result the meal's running total. Its `Day` scope then
 * takes the day, drops the edited meal's whole slot, and puts that running total in its
 * place. So whatever is missing from this list is missing from the day figure too, and a
 * day figure that is quietly too low still draws a perfectly ordinary bar.
 *
 * Until now the scanner passed an empty list, which was harmless only because it also
 * passed no day: with no day there was no `Day` scope to be wrong. Handing the day over
 * without fixing the list would have subtracted the meal and added back one food.
 *
 * Two groups belong in the answer, and the second is the one that is easy to forget:
 *
 * - **The meal's own ingredients, but only when the scan will KEEP them.** The scanner
 *   runs in one of two modes. `add` appends the scanned foods to what the meal already
 *   holds; `replace` throws those away first (`cs-day-items` opens it that way whenever
 *   the meal is not empty, and its result handler clears `meal.ingredients` before
 *   adding). Passing them in replace mode would count food the save is about to delete.
 * - **The other scanned foods.** A photo gives several foods at once and the user adjusts
 *   them one at a time, so while row 2 is open rows 1 and 3 are in neither the meal nor
 *   the running total. They are going to be saved with row 2, so they count. Only rows
 *   that will actually be saved: `addToMeal` ships the rows that are matched, ticked and
 *   carry a food id, and this applies the same test rather than a similar one.
 *
 * The row being adjusted is left out because it travels separately as `pendingIngredient`
 * -- the portion screen adds it itself, so including it here would count it twice. It is
 * identified by OBJECT IDENTITY, not by its name: the photo model can return the same name
 * twice (two eggs on one plate become two rows called "egg"), and matching on the name would
 * then drop both of them from the total -- quietly, and by exactly the kind of amount this
 * whole function exists to keep correct.
 *
 * Kept as a plain function, apart from the page, for the same reason
 * `sumDayNutrientsWithEditedMeal` is: nothing on screen catches this being wrong. Every
 * bar still draws, just from a smaller number than the truth.
 *
 * @param rows every row of the scanner's result list, in any order.
 * @param adjustedRow the row the user tapped, excluded by identity. Pass the very object
 *   from `rows`; a copy of it with the same fields would not be excluded.
 *   Undefined when no row is being adjusted, as when the user adds a food the scan missed
 *   (#6807): then every ticked row counts.
 * @param mealIngredients what the meal holds right now, from the page that opened the
 *   scanner. Undefined when the caller did not pass any.
 * @param isReplaceMode whether this scan will replace the meal's ingredients rather than
 *   add to them. Read from the scanner's own mode, never from the caller, so a caller
 *   that passes the meal's ingredients in replace mode cannot cause a double count.
 */
export function mealIngredientsBesidesTheAdjustedRow(
  rows: ScannedRow[],
  adjustedRow: ScannedRow | undefined,
  mealIngredients: Ingredient[] | undefined,
  isReplaceMode: boolean,
): Ingredient[] {
  // what survives the save, from what the meal already had
  const kept: Ingredient[] = isReplaceMode ? [] : (mealIngredients || []);

  // and every other scanned food that will be saved alongside the one being adjusted
  const otherScanned: IngredientReal[] = rows
    .filter(row => row !== adjustedRow && row.matched && row.checked && !!row.foodId)
    .map(row => ({
      type: IngredientType.Real,
      foodId: row.foodId,
      name: row.foodHeader.name,
      weight_g: row.estimatedGrams,
    }));

  return [...kept, ...otherScanned];
}
